// frontend/src/services/analytics.js

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.debugMode = process.env.REACT_APP_ENV === 'development';
    this.measurementId = this.getMeasurementId();
    this.queue = []; // Queue events before initialization
  }

  getMeasurementId() {
    const env = process.env.REACT_APP_ENV || 'development';
    
    if (env === 'production') {
      return process.env.REACT_APP_GA4_MEASUREMENT_ID_PROD ;
    } else {
      return process.env.REACT_APP_GA4_MEASUREMENT_ID_DEV ;
    }
  }

  /**
   * Initialize Google Analytics 4
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Load gtag script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', this.measurementId, {
        // Privacy settings
        anonymize_ip: true,
        allow_google_signals: true,
        allow_ad_personalization_signals: true,
        
        // Custom dimensions mapping
        custom_map: {
          'custom_parameter_1': 'user_plan',
          'custom_parameter_2': 'company_size', 
          'custom_parameter_3': 'use_case',
          'custom_parameter_4': 'auth_method',
          'custom_parameter_5': 'user_role'
        },

        // Debug mode for development
        debug_mode: this.debugMode,
        
        // Cookie settings
        cookie_flags: 'SameSite=None;Secure',
        
        // Enhanced measurement
        enhanced_measurement: {
          scrolls: true,
          outbound_clicks: true,
          site_search: true,
          video_engagement: true,
          file_downloads: true
        }
      });

      this.isInitialized = true;
      
      // Process queued events
      this.processQueue();
      
      if (this.debugMode) {
        console.log('🔍 Analytics initialized:', {
          measurementId: this.measurementId,
          environment: process.env.REACT_APP_ENV
        });
      }

    } catch (error) {
      console.error('❌ Analytics initialization failed:', error);
    }
  }

  /**
   * Process queued events after initialization
   */
  processQueue() {
    while (this.queue.length > 0) {
      const { method, args } = this.queue.shift();
      this[method](...args);
    }
  }

  /**
   * Check if analytics is ready
   */
  isReady() {
    return this.isInitialized && window.gtag;
  }

  /**
   * Queue event if not ready, otherwise send immediately
   */
  queueOrSend(method, ...args) {
    if (this.isReady()) {
      this[method](...args);
    } else {
      this.queue.push({ method, args });
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(userId, properties = {}) {
    if (!this.isReady()) {
      this.queueOrSend('setUserProperties', userId, properties);
      return;
    }

    window.gtag('config', this.measurementId, {
      user_id: userId,
      custom_map: {
        user_plan: properties.paymentStatus || 'free',
        company_size: properties.companySize || 'unknown',
        use_case: properties.useCase || 'unknown',
        auth_method: properties.authMethod || 'unknown',
        user_role: properties.role || 'user'
      }
    });

    if (this.debugMode) {
      console.log('👤 User properties set:', { userId, properties });
    }
  }

  /**
   * Track page views
   */
  trackPageView(pagePath, pageTitle, additionalParams = {}) {
    if (!this.isReady()) {
      this.queueOrSend('trackPageView', pagePath, pageTitle, additionalParams);
      return;
    }

    window.gtag('config', this.measurementId, {
      page_path: pagePath,
      page_title: pageTitle,
      ...additionalParams
    });

    if (this.debugMode) {
      console.log('📄 Page view tracked:', { pagePath, pageTitle, additionalParams });
    }
  }

  /**
   * Track custom events
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isReady()) {
      this.queueOrSend('trackEvent', eventName, parameters);
      return;
    }

    // Sanitize parameters to avoid sending sensitive data
    const sanitizedParams = this.sanitizeParameters(parameters);

    window.gtag('event', eventName, sanitizedParams);

    if (this.debugMode) {
      console.log('🎯 Event tracked:', { eventName, parameters: sanitizedParams });
    }
  }

  /**
   * Sanitize parameters to remove sensitive data
   */
  sanitizeParameters(params) {
    const sanitized = { ...params };
    
    // Remove sensitive fields
    const sensitiveFields = ['email', 'password', 'token', 'api_key', 'document_content'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        delete sanitized[field];
      }
    });

    // Hash document IDs for privacy
    if (sanitized.document_id && typeof sanitized.document_id === 'string') {
      sanitized.document_id = this.hashString(sanitized.document_id);
    }

    return sanitized;
  }

  /**
   * Simple hash function for IDs
   */
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Track authentication events
   */
  trackAuth(action, method, success = true, additionalParams = {}) {
    this.trackEvent(action, {
      event_category: 'auth',
      method,
      success,
      ...additionalParams
    });
  }

  /**
   * Track document workflow events
   */
  trackDocument(action, documentData = {}) {
    this.trackEvent(action, {
      event_category: 'document',
      file_type: documentData.fileType,
      file_size_mb: documentData.fileSize ? Math.round(documentData.fileSize / 1024 / 1024 * 100) / 100 : undefined,
      document_id: documentData.id,
      status: documentData.status,
      ...documentData.additionalParams
    });
  }

  /**
   * Track signature events
   */
  trackSignature(action, signatureData = {}) {
    this.trackEvent(action, {
      event_category: 'signature',
      signature_type: signatureData.type,
      page_number: signatureData.page,
      time_to_sign_seconds: signatureData.timeToSign,
      is_mobile: /Mobi|Android/i.test(navigator.userAgent),
      ...signatureData.additionalParams
    });
  }

  /**
   * Track payment events
   */
  trackPayment(action, paymentData = {}) {
    const params = {
      event_category: 'payment',
      currency: paymentData.currency || 'USD',
      value: paymentData.value,
      ...paymentData.additionalParams
    };

    // Special handling for purchase events
    if (action === 'purchase' && paymentData.transactionId) {
      params.transaction_id = paymentData.transactionId;
      params.items = paymentData.items || [];
    }

    this.trackEvent(action, params);
  }

  /**
   * Track feature usage
   */
  trackFeature(featureName, action = 'used', additionalParams = {}) {
    this.trackEvent('feature_interaction', {
      event_category: 'feature',
      feature_name: featureName,
      action,
      ...additionalParams
    });
  }

  /**
   * Track onboarding progress
   */
  trackOnboarding(stepName, stepNumber, totalSteps, completed = false, additionalParams = {}) {
    this.trackEvent(completed ? 'onboarding_step_complete' : 'onboarding_step_start', {
      event_category: 'onboarding',
      step_name: stepName,
      step_number: stepNumber,
      total_steps: totalSteps,
      completed,
      ...additionalParams
    });
  }

  /**
   * Track engagement events
   */
  trackEngagement(action, element, section, additionalParams = {}) {
    this.trackEvent('engagement', {
      event_category: 'engagement',
      action,
      element,
      section,
      ...additionalParams
    });
  }

  /**
   * Track errors
   */
  trackError(errorType, errorMessage, location, additionalParams = {}) {
    this.trackEvent('error_occurred', {
      event_category: 'error',
      error_type: errorType,
      error_message: errorMessage?.substring(0, 100), // Limit length
      location,
      ...additionalParams
    });
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metricName, value, additionalParams = {}) {
    this.trackEvent('performance_metric', {
      event_category: 'performance',
      metric_name: metricName,
      value,
      ...additionalParams
    });
  }

  /**
   * Disable analytics (for opt-out)
   */
  disable() {
    window[`ga-disable-${this.measurementId}`] = true;
    
    if (this.debugMode) {
      console.log('🚫 Analytics disabled');
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
